import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Link, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useCourseThumbnail } from "@/lib/hooks/use-course-thumbnail";

interface CourseThumbnailUploadProps {
  courseId?: string;
  currentUrl?: string | null;
  onChange: (url: string | null) => void;
  className?: string;
}

export default function CourseThumbnailUpload({ 
  courseId, 
  currentUrl, 
  onChange,
  className = "",
}: CourseThumbnailUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState("");
  const [displayedUrl, setDisplayedUrl] = useState(currentUrl);
  const { 
    uploadThumbnail, 
    deleteThumbnail, 
    isUploading, 
    isDeleting, 
    isStorageUrl,
    uploadedImageUrl,
    isUploadSuccess
  } = useCourseThumbnail({ 
    courseId,
    onSuccess: (url) => {
      onChange(url);
      setDisplayedUrl(url);
    }
  });

  // Update displayed URL when current URL changes
  useEffect(() => {
    if (currentUrl !== displayedUrl) {
      // If it's a blob URL (optimistic), show it immediately
      if (currentUrl?.startsWith('blob:')) {
        setDisplayedUrl(currentUrl);
      } else if (currentUrl) {
        // If it's a real URL, preload it first for smooth transition
        const img = new Image();
        img.onload = () => setDisplayedUrl(currentUrl);
        img.onerror = () => setDisplayedUrl(currentUrl);
        img.src = currentUrl;
      } else {
        setDisplayedUrl(null);
      }
    }
  }, [currentUrl, displayedUrl]);

  // Handle successful upload during course creation (when no courseId yet)
  useEffect(() => {
    if (isUploadSuccess && uploadedImageUrl && !courseId) {
      onChange(uploadedImageUrl);
      setDisplayedUrl(uploadedImageUrl);
    }
  }, [isUploadSuccess, uploadedImageUrl, onChange, courseId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Create optimistic preview
    const previewUrl = URL.createObjectURL(file);
    setDisplayedUrl(previewUrl);
    
    uploadThumbnail(file);
    
    // Clear the input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleUrlSubmit = () => {
    if (!urlInput) {
      toast.error('Please enter a URL');
      return;
    }

    try {
      new URL(urlInput);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    // If there's an existing storage file, delete it first
    if (currentUrl && isStorageUrl(currentUrl)) {
      deleteThumbnail(currentUrl);
    }

    onChange(urlInput);
    setDisplayedUrl(urlInput);
    setUrlInput("");
    toast.success('Thumbnail URL updated!');
  };

  const handleDelete = () => {
    if (!currentUrl) return;
    
    if (isStorageUrl(currentUrl)) {
      deleteThumbnail(currentUrl);
    } else {
      onChange(null);
      setDisplayedUrl(null);
      toast.success('Thumbnail removed!');
    }
  };

  const defaultTab = currentUrl && !isStorageUrl(currentUrl) ? "url" : "upload";

  return (
    <div className={`space-y-4 ${className}`}>
      <Label>Course Thumbnail</Label>
      
      {displayedUrl && (
        <Card className="p-4">
          <div className="space-y-4">
            <div className="relative group">
              <img
                src={displayedUrl}
                alt="Course thumbnail"
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isDeleting || isUploading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
            {currentUrl && isStorageUrl(currentUrl) && (
              <p className="text-sm text-muted-foreground">
                Uploaded image
              </p>
            )}
          </div>
        </Card>
      )}

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upload">
            <Upload className="h-4 w-4 mr-2" />
            Upload Image
          </TabsTrigger>
          <TabsTrigger value="url">
            <Link className="h-4 w-4 mr-2" />
            Use URL
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload" className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <ImageIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <Button
              type="button"
              variant="outline"
              onClick={handleUploadClick}
              disabled={isUploading || isDeleting}
            >
              {isUploading ? 'Uploading...' : 'Choose Image'}
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              PNG, JPG up to 5MB
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="url" className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="https://example.com/image.jpg"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              type="url"
              disabled={isDeleting}
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={!urlInput || isDeleting}
            >
              Set URL
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Enter the URL of an image hosted elsewhere
          </p>
        </TabsContent>
      </Tabs>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}